import { z } from 'zod';
import { AuditMeta, DocId, TenantId, UserRole } from '../common.js';
/**
 * A user belongs to a tenant. SSO-authenticated users are keyed by their
 * Microsoft Entra subject (sub claim); dev-token users use synthetic IDs.
 *
 * Firestore path: tenants/{tenantId}/users/{userId}
 */
export const UserSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    email: z.string().email().toLowerCase(),
    displayName: z.string().min(1).max(200),
    role: UserRole,
    /**
     * Microsoft Entra object ID (aka oid claim). Stable per user across sessions.
     * Undefined for dev-token users.
     */
    entraOid: z.string().uuid().optional(),
    status: z.enum(['active', 'invited', 'deactivated']),
    lastLoginAt: z.string().datetime({ offset: true }).optional(),
    /**
     * Directory profile attributes pulled from Microsoft Graph (`/me`) at login.
     * Not in the ID token — Entra only exposes these via Graph. Best-effort:
     * absent when Graph is unreachable or the field is unset on the directory
     * profile. jobTitle/department/officeLocation are forwarded to the Knowledge
     * Agent as query context so answers can be role-aware. `photoDataUrl` is a
     * small (96px) base64 data URL used for the UI avatar only — never sent to
     * the agent.
     */
    jobTitle: z.string().max(200).optional(),
    department: z.string().max(200).optional(),
    officeLocation: z.string().max(200).optional(),
    photoDataUrl: z.string().max(200_000).optional(),
});
/**
 * Invite a new user. Role must be valid for the tenant type (enforced at service layer).
 */
export const InviteUserInput = z.object({
    email: z.string().email().toLowerCase(),
    displayName: z.string().min(1).max(200),
    role: UserRole,
});
/**
 * Safe public projection for /api/me responses — never includes audit fields
 * or cross-tenant data.
 */
export const PublicUserProjection = UserSchema.pick({
    id: true,
    tenantId: true,
    email: true,
    displayName: true,
    role: true,
    status: true,
    jobTitle: true,
    department: true,
    officeLocation: true,
    photoDataUrl: true,
});
/**
 * /api/me — the caller's own projection plus the fields the Profile page
 * shows. `memberSince` is the user doc's `createdAt` under a different name
 * so the "audit fields never leak" invariant on the projection stays
 * checkable (createdBy/updatedBy are still never exposed). Both timestamps
 * are absent for dev tokens and API keys (no user doc behind them).
 * `accessGroups` are the capability tags resolved from Entra groups at
 * sign-in — the same list the retrieval visibility filter uses.
 */
export const MeUserProjection = PublicUserProjection.extend({
    memberSince: z.string().datetime({ offset: true }).optional(),
    lastLoginAt: z.string().datetime({ offset: true }).optional(),
    accessGroups: z.array(z.string()).optional(),
});
//# sourceMappingURL=user.js.map